/**
 * StackedCard
 *
 * Animation model — two-phase state machine:
 *
 *   IDLE       Normal resting state.
 *              front layer: .stacked-layer--front   (z:2, full opacity)
 *              back  layer: .stacked-layer--back    (z:1, offset/dimmed)
 *
 *   ANIMATING  Triggered immediately on toggle click.
 *              Three divs rendered simultaneously:
 *                outgoing  : .stacked-layer--outgoing        — old front, plays stackCardOut keyframe
 *                            (arcs up-right like a card dealt off the deck, fades out)
 *                incoming  : .stacked-layer--back-animating  — old back, CSS transition to (0,0)/full opacity
 *                            (slides forward to front position simultaneously)
 *                new-back  : .stacked-layer--back            — new back card pre-rendered at offset (invisible during anim)
 *
 *              animationend on the outgoing div, filtered by e.animationName === 'stackCardOut'
 *              so child animation bubbling (cardScaleUp, flicker*, etc.) is ignored.
 *              On valid animationend → flip frontSlot → back to IDLE.
 *
 * Card data is frozen into a ref at the moment toggle is clicked so that React
 * re-renders during the ANIMATING phase cannot change which card is shown where.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { trackEvent } from '../../utils/analytics'
import { StarcadeFrame } from './DSL_Cards'
import { playSound } from '../../utils/soundFX'

function ArrowIcon() {
  return (
    <svg
      className="stacked-toggle-icon"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6,15 12,9 18,15" />
    </svg>
  )
}

export default function StackedCard({ item, section = 'starcade', isTransitioning, shouldFlicker, index, dragDistanceRef }) {
  const [frontSlot, setFrontSlot] = useState(0)
  const [phase, setPhase] = useState('idle') // 'idle' | 'animating-pre' | 'animating'
  const wrapperRef = useRef(null)
  const frontLayerRef = useRef(null)

  // Frozen snapshot of card data captured at the moment toggle fires.
  // Prevents mid-animation state changes from corrupting which card is shown.
  const frozenRef = useRef(null)
  // Screen-space rect of the wrapper captured on toggle — used to position the
  // outgoing layer as position:fixed so it escapes all overflow:clip ancestors.
  const outgoingRectRef = useRef(null)

  // 'animating-pre' → 'animating' after one rAF so the browser paints the incoming
  // card at its offset position before the CSS transition kicks in.
  useEffect(() => {
    if (phase === 'animating-pre') {
      const id = requestAnimationFrame(() => setPhase('animating'))
      return () => cancelAnimationFrame(id)
    }
  }, [phase])

  // Stable random values — computed once on mount
  const scaleDuration = useRef((0.8 + Math.random() * 0.4) * 0.4)
  const flickerDelays = useRef({
    outer: Math.random() * 0.3,
    gradient: Math.random() * 0.3,
    text: Math.random() * 0.3
  })

  const cardData = useMemo(() => [
    {
      id: item.id,
      title: item.title,
      imageSrc: item.imageSrc,
      imageScale: item.imageScale ?? 1,
      imageOffsetX: item.imageOffsetX ?? 0,
      imageOffsetY: item.imageOffsetY ?? 0,
      buttonUrl: item.buttonUrl ?? '',
      badge: item.badge ?? null,
      logo: item.logo ?? null,
      logoWidth: item.logoWidth ?? 48,
      logoOffsetY: item.logoOffsetY ?? 0,
      titleOffsetX: item.titleOffsetX ?? 0,
      titleOffsetY: item.titleOffsetY ?? 0,
      titleVisibleOnCard: item.titleVisibleOnCard !== false
    },
    {
      id: item.secondCard?.id ?? item.id,
      title: item.secondCard?.title ?? item.title,
      imageSrc: item.secondCard?.imageSrc ?? item.imageSrc,
      imageScale: item.secondCard?.imageScale ?? 1,
      imageOffsetX: item.secondCard?.imageOffsetX ?? 0,
      imageOffsetY: item.secondCard?.imageOffsetY ?? item.imageOffsetY ?? 0,
      buttonUrl: item.secondCard?.buttonUrl ?? '',
      badge: item.secondCard?.badge ?? item.badge ?? null,
      logo: item.secondCard?.logo ?? item.logo ?? null,
      logoWidth: item.secondCard?.logoWidth ?? item.logoWidth ?? 48,
      logoOffsetY: item.secondCard?.logoOffsetY ?? item.logoOffsetY ?? 0,
      titleOffsetX: item.secondCard?.titleOffsetX ?? item.titleOffsetX ?? 0,
      titleOffsetY: item.secondCard?.titleOffsetY ?? item.titleOffsetY ?? 0,
      titleVisibleOnCard: (item.secondCard?.titleVisibleOnCard ?? item.titleVisibleOnCard) !== false
    }
  ], [item])

  const backSlot  = frontSlot === 0 ? 1 : 0
  const frontCard = cardData[frontSlot]
  const backCard  = cardData[backSlot]

  const paddedIndex = String(index + 1).padStart(3, '0')

  // In IDLE, derive labels from live state. In ANIMATING, use frozen snapshot.
  const isAnimating = phase === 'animating-pre' || phase === 'animating'
  const activeFront = isAnimating ? frozenRef.current.frontCard : frontCard
  const activeBack  = isAnimating ? frozenRef.current.backCard  : backCard

  const frontLabel = `[ ${paddedIndex}-A ]  ${activeFront.title.toUpperCase()}`
  const backLabel  = `[ ${paddedIndex}-B ]  ${activeBack.title.toUpperCase()}`

  const handleToggle = useCallback((e) => {
    e.stopPropagation()
    if (phase === 'animating-pre' || phase === 'animating') return // ignore re-clicks during animation
    playSound('DataBeep')

    // Capture screen-space rect of the front layer (excludes wrapper padding)
    if (frontLayerRef.current) {
      outgoingRectRef.current = frontLayerRef.current.getBoundingClientRect()
    }

    // Freeze the card data snapshot before any state update
    const currentFrontSlot = frontSlot
    const currentBackSlot  = currentFrontSlot === 0 ? 1 : 0
    const nextFrontSlot    = currentBackSlot
    const nextBackSlot     = currentFrontSlot

    frozenRef.current = {
      frontCard:     cardData[currentFrontSlot],
      backCard:      cardData[currentBackSlot],
      nextFrontCard: cardData[nextFrontSlot],
      nextBackCard:  cardData[nextBackSlot],
      nextFrontSlot
    }

    setPhase('animating-pre')
  }, [phase, frontSlot, cardData])

  const handleAnimationEnd = useCallback((e) => {
    // Ignore bubbled animationend events from child elements (cardScaleUp, flicker*, etc.)
    if (e.animationName !== 'stackCardOut') return

    const next = frozenRef.current.nextFrontSlot
    frozenRef.current = null
    setFrontSlot(next)
    setPhase('idle')
  }, [])

  const handleCardClick = useCallback(() => {
    if (dragDistanceRef.current > 5) return
    if (!activeFront.buttonUrl) return
    trackEvent(`${section}_${activeFront.id}`)
    playSound('DataPoint_Open')
    window.open(activeFront.buttonUrl, '_blank', 'noopener,noreferrer')
  }, [activeFront, dragDistanceRef, section])

  const wrapperStyle = {
    cursor: 'pointer',
    '--flicker-delay-outer':    `${flickerDelays.current.outer}s`,
    '--flicker-delay-gradient': `${flickerDelays.current.gradient}s`,
    '--flicker-delay-text':     `${flickerDelays.current.text}s`,
    '--card-scale-duration':    `${scaleDuration.current.toFixed(3)}s`,
    '--image-delay':            `${(scaleDuration.current * 0.6).toFixed(3)}s`
  }

  return (
    <div
      ref={wrapperRef}
      className={`starcade-card stacked-card-wrapper ${shouldFlicker ? 'flickering' : ''}`}
      onClick={handleCardClick}
      style={wrapperStyle}
    >
      {!isAnimating ? (
        <>
          {/* IDLE: back card at offset/dimmed resting position */}
          <div className="stacked-layer stacked-layer--back">
            <StarcadeFrame
              isTransitioning={false}
              shouldFlicker={false}
              index={index}
              svgId={`${index}-back`}
              labelOverride={backLabel}
              imageSrc={backCard.imageSrc}
              imageScale={backCard.imageScale}
              imageOffsetX={backCard.imageOffsetX}
              imageOffsetY={backCard.imageOffsetY}
              badge={backCard.badge}
              logo={backCard.logo}
              logoWidth={backCard.logoWidth}
              logoOffsetY={backCard.logoOffsetY}
              titleOffsetX={backCard.titleOffsetX}
              titleOffsetY={backCard.titleOffsetY}
              showCenterTitle={backCard.titleVisibleOnCard}
            />
          </div>

          {/* IDLE: front card — button lives here so it travels with this layer */}
          <div ref={frontLayerRef} className="stacked-layer stacked-layer--front">
            <StarcadeFrame
              isTransitioning={isTransitioning}
              shouldFlicker={shouldFlicker}
              index={index}
              svgId={`${index}-front`}
              labelOverride={frontLabel}
              imageSrc={frontCard.imageSrc}
              imageScale={frontCard.imageScale}
              imageOffsetX={frontCard.imageOffsetX}
              imageOffsetY={frontCard.imageOffsetY}
              badge={frontCard.badge}
              logo={frontCard.logo}
              logoWidth={frontCard.logoWidth}
              logoOffsetY={frontCard.logoOffsetY}
              titleOffsetX={frontCard.titleOffsetX}
              titleOffsetY={frontCard.titleOffsetY}
              showCenterTitle={frontCard.titleVisibleOnCard}
            />
            <button
              className="stacked-toggle-btn"
              onClick={handleToggle}
              aria-label="Switch card"
              title={`Switch to: ${backCard.title}`}
            >
              <ArrowIcon />
            </button>
          </div>
        </>
      ) : (
        <>
          {/* ANIMATING: new back card pre-rendered at offset — visible once animation completes */}
          <div className="stacked-layer stacked-layer--back">
            <StarcadeFrame
              isTransitioning={false}
              shouldFlicker={false}
              index={index}
              svgId={`${index}-back`}
              labelOverride={`[ ${paddedIndex}-B ]  ${frozenRef.current.nextBackCard.title.toUpperCase()}`}
              imageSrc={frozenRef.current.nextBackCard.imageSrc}
              imageScale={frozenRef.current.nextBackCard.imageScale}
              imageOffsetX={frozenRef.current.nextBackCard.imageOffsetX}
              imageOffsetY={frozenRef.current.nextBackCard.imageOffsetY}
              badge={frozenRef.current.nextBackCard.badge}
              logo={frozenRef.current.nextBackCard.logo}
              logoWidth={frozenRef.current.nextBackCard.logoWidth}
              logoOffsetY={frozenRef.current.nextBackCard.logoOffsetY}
              titleOffsetX={frozenRef.current.nextBackCard.titleOffsetX}
              titleOffsetY={frozenRef.current.nextBackCard.titleOffsetY}
              showCenterTitle={frozenRef.current.nextBackCard.titleVisibleOnCard}
            />
          </div>

          {/* ANIMATING: incoming card (was back) — starts at offset, transitions to front position.
              animating-pre: still at .stacked-layer--back (offset) — browser paints this first.
              animating:     class swaps to .stacked-layer--back-animating — transition fires. */}
          <div className={`stacked-layer ${phase === 'animating' ? 'stacked-layer--back-animating' : 'stacked-layer--back'}`}>
            <StarcadeFrame
              isTransitioning={false}
              shouldFlicker={false}
              index={index}
              svgId={`${index}-incoming`}
              labelOverride={`[ ${paddedIndex}-A ]  ${frozenRef.current.nextFrontCard.title.toUpperCase()}`}
              imageSrc={frozenRef.current.nextFrontCard.imageSrc}
              imageScale={frozenRef.current.nextFrontCard.imageScale}
              imageOffsetX={frozenRef.current.nextFrontCard.imageOffsetX}
              imageOffsetY={frozenRef.current.nextFrontCard.imageOffsetY}
              badge={frozenRef.current.nextFrontCard.badge}
              logo={frozenRef.current.nextFrontCard.logo}
              logoWidth={frozenRef.current.nextFrontCard.logoWidth}
              logoOffsetY={frozenRef.current.nextFrontCard.logoOffsetY}
              titleOffsetX={frozenRef.current.nextFrontCard.titleOffsetX}
              titleOffsetY={frozenRef.current.nextFrontCard.titleOffsetY}
              showCenterTitle={frozenRef.current.nextFrontCard.titleVisibleOnCard}
            />
          </div>

          {/* ANIMATING: outgoing card — rendered position:fixed at the card's screen coords
               so it escapes all overflow:clip ancestors and can animate freely off-screen */}
          <div
            className="stacked-layer stacked-layer--outgoing"
            onAnimationEnd={handleAnimationEnd}
            style={outgoingRectRef.current ? {
              position: 'fixed',
              top: outgoingRectRef.current.top,
              left: outgoingRectRef.current.left,
              width: outgoingRectRef.current.width,
              height: outgoingRectRef.current.height,
            } : undefined}
          >
            <StarcadeFrame
              isTransitioning={false}
              shouldFlicker={false}
              index={index}
              svgId={`${index}-outgoing`}
              labelOverride={frontLabel}
              imageSrc={frozenRef.current.frontCard.imageSrc}
              imageScale={frozenRef.current.frontCard.imageScale}
              imageOffsetX={frozenRef.current.frontCard.imageOffsetX}
              imageOffsetY={frozenRef.current.frontCard.imageOffsetY}
              badge={frozenRef.current.frontCard.badge}
              logo={frozenRef.current.frontCard.logo}
              logoWidth={frozenRef.current.frontCard.logoWidth}
              logoOffsetY={frozenRef.current.frontCard.logoOffsetY}
              titleOffsetX={frozenRef.current.frontCard.titleOffsetX}
              titleOffsetY={frozenRef.current.frontCard.titleOffsetY}
              showCenterTitle={frozenRef.current.frontCard.titleVisibleOnCard}
              width={outgoingRectRef.current?.width}
              height={outgoingRectRef.current?.height}
            />
            <button
              className="stacked-toggle-btn"
              onClick={handleToggle}
              aria-label="Switch card"
              style={{ pointerEvents: 'none' }}
            >
              <ArrowIcon />
            </button>
          </div>
        </>
      )}

    </div>
  )
}
